import { type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { SelectOption, StanFilterConfig, StanFilterValue } from '../types';
import { SvgIcon } from '@/types';
import { DEFAULT_OPERATORS, DEFAULT_REDUCE_KEY } from '../constants';

type UseSelectFilterArgs = {
    filterItem: StanFilterConfig;
    onApply: (filters: StanFilterValue[]) => void;
    onClearFilter?: (id: string) => void;
    disabled?: boolean;
    closeMenu: () => void;
};

interface UseSelectFilterReturn {
    summary: string;
    hasSummary: boolean;
    dropdownOptions: DropdownMenuOption[];
    currentValue: DropdownMenuOption | null;
    onSelect: (val: { value: string }) => void;
    clearFilters: (event?: MouseEvent) => void;
    // New functionality extracted from component
    handleOptionClick: (value: string) => void;
    getIconConfig: (
        icon: string | SvgIcon,
        label: string
    ) =>
        | { type: 'img'; src: string; alt: string; width: number; height: number }
        | { type: 'component'; component: SvgIcon; removeFill: boolean; width: number; height: number };
}

export const useSelectFilter = ({
    filterItem,
    onApply,
    onClearFilter,
    closeMenu,
}: UseSelectFilterArgs): UseSelectFilterReturn => {
    const reduceKey = filterItem.customDropdownReduce || DEFAULT_REDUCE_KEY;

    const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null);
    const [appliedOption, setAppliedOption] = useState<SelectOption | null>(null);

    const summary = useMemo(() => {
        if (!appliedOption) return '';
        return appliedOption.label ?? '';
    }, [appliedOption]);

    const resetState = useCallback(() => {
        setSelectedOption(null);
        setAppliedOption(null);
    }, []);

    // Initialize select state from filterItem.initFilterValue
    useEffect(() => {
        const init = filterItem.initFilterValue;
        if (init === null) {
            resetState();
            closeMenu();
        } else if (init) {
            const opts = filterItem.customDropdownOptions || [];
            const match = opts.find((o: SelectOption) => String(o[reduceKey]) === String(init.value));
            setSelectedOption(match || null);
            setAppliedOption(match || null);
        }
    }, [filterItem.initFilterValue, filterItem.customDropdownOptions, reduceKey, resetState, closeMenu]);

    const options: SelectOption[] = useMemo(
        () => filterItem.customDropdownOptions || [],
        [filterItem.customDropdownOptions]
    );

    const dropdownOptions: DropdownMenuOption[] = useMemo(
        () =>
            (options || []).map(opt => ({
                label: opt.label ?? String(opt[reduceKey] ?? ''),
                value: String(opt[reduceKey] ?? opt.label ?? ''),
                icon: opt.icon,
            })),
        [options, reduceKey]
    );

    const optionByValue = useMemo(() => {
        const map = new Map<string, SelectOption>();
        for (const opt of options || []) {
            const key = String(opt[reduceKey] ?? opt.label ?? '');
            map.set(key, opt);
        }
        return map;
    }, [options, reduceKey]);

    const currentValue = selectedOption
        ? {
              label: selectedOption.label ?? String(selectedOption[reduceKey] ?? ''),
              value: String(selectedOption[reduceKey] ?? selectedOption.label ?? ''),
          }
        : null;

    const clearFilters = useCallback(
        (event?: MouseEvent) => {
            event?.stopPropagation();
            resetState();
            onApply([]);
            onClearFilter?.(filterItem.id);
            closeMenu();
        },
        [closeMenu, filterItem.id, onApply, onClearFilter, resetState]
    );

    const onSelect = useCallback(
        (val: { value: string }) => {
            const raw = optionByValue.get(val.value);
            if (!raw) return;
            setSelectedOption(raw);

            const valueSource = raw[reduceKey] ?? raw.label ?? '';
            // Keep numbers as numbers, convert others to strings
            const value = typeof valueSource === 'number' ? valueSource : String(valueSource);
            const out: StanFilterValue[] = [
                {
                    id: filterItem.id,
                    // Use 'like' operator for all select filters (matches Vue implementation)
                    // Previously used 'equals' for product filter, but Cash Out filter requires 'like'
                    // to properly match negative product IDs. All select filters now use 'like' for consistency.
                    operator: DEFAULT_OPERATORS.SELECT,
                    value,
                    type: 'select',
                },
            ];
            onApply(out);
            setAppliedOption(raw);
            closeMenu();
        },
        [closeMenu, filterItem.id, onApply, optionByValue, reduceKey]
    );

    /**
     * Handles option click with proper value extraction
     * Extracted from component for better separation of concerns
     */
    const handleOptionClick = (value: string) => {
        onSelect({ value });
    };

    /**
     * Returns icon configuration for rendering
     * Extracted from component for better separation of concerns
     */
    const getIconConfig = useCallback((icon: string | SvgIcon, label: string) => {
        if (typeof icon === 'string') {
            return { type: 'img' as const, src: icon, alt: label, width: 16, height: 16 };
        }
        return { type: 'component' as const, component: icon, removeFill: true, width: 16, height: 16 };
    }, []);

    const hasSummary = summary.length > 0;

    return {
        summary,
        hasSummary,
        dropdownOptions,
        currentValue,
        onSelect,
        clearFilters,
        // New functionality extracted from component
        handleOptionClick,
        getIconConfig,
    };
};
